import { formatSaved, savingsClaim, type MeResponse } from '@nolens/domain';
import { formatNumber } from '@nolens/i18n';
import { cx, useLocale } from '@nolens/ui';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { GettingStartedCard } from '../components/GettingStarted';
import { GuideCollection, useLibraryView, ViewToggle } from '../components/GuideCard';
import { ClockIcon, EyeIcon, MyGuidesIcon } from '../components/Icons';
import { QueryView } from '../components/Query';
import { useAllGuides, useMeSummary } from '../lib/queries';
import { workspaceText } from '../i18n/workspace';
import { EmptyLibrary, LibrarySearch, matches, WithMe } from './workspace';

const RECENT = 8;

const spacesOf = (me: MeResponse) => new Map(me.workspaces.map((w) => [w.id, w]));

/**
 * Início: os guias mais recentes de todos os espaços, os números da pessoa (guias gravados,
 * leituras, tempo poupado) e os primeiros passos enquanto houver algum por fazer.
 */
export function HomeDashboard() {
  const t = workspaceText(useLocale());
  return <WithMe crumbs={() => [{ label: t.home }]}>{(me) => <Home me={me} />}</WithMe>;
}

function Home({ me }: { me: MeResponse }) {
  const t = workspaceText(useLocale());
  const recent = useAllGuides({ limit: RECENT });
  const [view, setView] = useLibraryView();
  const spaces = useMemo(() => spacesOf(me), [me]);
  const any = me.workspaces.some((w) => w.guides > 0);
  return (
    <div className="grid gap-10" data-home>
      <header>
        <h1 className="text-[26px] font-semibold tracking-tight">
          {any ? t.welcomeBack : t.welcome}
        </h1>
        <p className="mt-1 text-[15px] text-text-2">{t.homeHint}</p>
      </header>
      <QueryView q={recent}>
        {(list) =>
          list.length === 0 ? (
            <EmptyLibrary kind="personal" role="owner" email={me.user.email} />
          ) : (
            <section aria-labelledby="recent">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 id="recent" className="text-lg font-semibold">
                  {t.recent}
                </h2>
                <ViewToggle value={view} onChange={setView} />
              </div>
              <GuideCollection guides={list} view={view} spaces={spaces} />
              <div className="mt-5 flex justify-center">
                <Link
                  to="/guias"
                  className="inline-flex h-9 items-center rounded-full px-4 text-sm font-medium shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-surface-1"
                >
                  {t.seeMyGuides}
                </Link>
              </div>
            </section>
          )
        }
      </QueryView>
      {any && <Numbers />}
      <GettingStartedCard me={me} />
    </div>
  );
}

function Tile({
  icon,
  label,
  value,
  note,
  accent,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  note?: string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 rounded-[16px] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--border)]">
      <span
        className={cx(
          'inline-flex size-9 shrink-0 items-center justify-center rounded-full',
          accent ? 'bg-accent-soft text-accent-text' : 'bg-surface-2 text-text-2',
        )}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[13px] text-text-2">{label}</p>
        <p
          className={cx(
            'mt-0.5 text-[26px] font-semibold leading-tight tracking-tight',
            accent && 'text-accent-text',
          )}
        >
          {value}
        </p>
        {note && <p className="mt-0.5 text-[12.5px] text-text-3">{note}</p>}
      </div>
    </div>
  );
}

/** Os números da pessoa: os guias que gravou, quantas vezes foram lidos e o tempo poupado. */
function Numbers() {
  const locale = useLocale();
  const t = workspaceText(locale);
  const s = useMeSummary();
  if (!s.data) return null;
  const d = s.data;
  return (
    <section aria-labelledby="numbers" data-home-summary>
      <h2 id="numbers" className="mb-4 text-lg font-semibold">
        {t.yourNumbers}
      </h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <Tile
          icon={<MyGuidesIcon />}
          label={t.guidesRecorded}
          value={formatNumber(locale, d.guides)}
          note={t.stepsDocumented(d.steps)}
        />
        <Tile
          icon={<EyeIcon />}
          label={t.reads30}
          value={formatNumber(locale, d.reads30)}
          note={t.readsAllTime(d.readsTotal)}
        />
        <Tile
          icon={<ClockIcon />}
          label={t.timeSaved}
          value={`≈ ${formatSaved(d.savedSeconds, locale)}`}
          note={t.estimate}
          accent
        />
      </div>
      <p className="mt-2 text-[12.5px] text-text-3">{savingsClaim(locale)}</p>
    </section>
  );
}

/** Os meus guias: os que a pessoa gravou, em todos os espaços onde está. */
export function MyGuidesPage() {
  const t = workspaceText(useLocale());
  return <WithMe crumbs={() => [{ label: t.myGuides }]}>{(me) => <MyGuides me={me} />}</WithMe>;
}

function MyGuides({ me }: { me: MeResponse }) {
  const t = workspaceText(useLocale());
  const guides = useAllGuides({ mine: true });
  const [q, setQ] = useState('');
  const [view, setView] = useLibraryView();
  const spaces = useMemo(() => spacesOf(me), [me]);
  return (
    <section>
      <h1 className="text-2xl font-semibold tracking-tight">{t.myGuides}</h1>
      <p className="mt-1 text-sm text-text-2">{t.myGuidesHint}</p>
      <div className="mt-6 flex items-center gap-3">
        <LibrarySearch value={q} onChange={setQ} />
        <ViewToggle value={view} onChange={setView} />
      </div>
      <div className="mt-5">
        <QueryView q={guides}>
          {(list) => {
            const shown = list.filter((g) => matches(g, q));
            if (!list.length)
              return <EmptyLibrary kind="personal" role="owner" email={me.user.email} />;
            if (!shown.length) return <p className="text-text-2">{t.noMatch(q)}</p>;
            return <GuideCollection guides={shown} view={view} spaces={spaces} />;
          }}
        </QueryView>
      </div>
    </section>
  );
}
